import { OBSERVATION_PANEL_NODE } from '@seen/core/observation-logic/documentUnits'
import {
  observationReferenceStrings,
  recordedObservationAnswers,
} from '@seen/core/observationReferences'
import type { Node as PMNode } from '@tiptap/pm/model'
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state'

// What typed or pasted input took out of the note: single records, and the
// groups of instrument panels (each item, subtotal, total and panel record).
export interface InputRemovedRecords {
  references: string[]
  groupIds: string[]
}

// Long enough to undo a slip of the keyboard before the record goes.
export const OBSERVATION_INPUT_REMOVAL_DELAY_MS = 1500

const OBSERVATION_INPUT_REMOVAL_KEY = new PluginKey('observationInputRemoval')

const GROUP_PREFIX = 'group:'

// Typing or pasting over a selection that holds recorded nodes skips the
// Backspace/Delete confirmation, so the records those nodes held are removed
// here once the input settles; a panel's records go by its group. A cut is a
// move and is left alone; a record that comes back (the paste that follows a
// cut, an undo) before the delay runs out is kept. Freezing, clearing and
// confirmed removal are not typed or pasted input, so they never reach this.
export function observationInputRemovalPlugin({
  typeName,
  onRemoveRecords,
  delayMs = OBSERVATION_INPUT_REMOVAL_DELAY_MS,
}: {
  typeName: string
  onRemoveRecords: (removed: InputRemovedRecords) => void
  delayMs?: number
}): Plugin {
  // Set by `handleTextInput` for the transaction the typed text produces,
  // which ProseMirror dispatches synchronously right after it.
  let typingOverRange = false
  // Keys from `recordKeys`: a record's reference, or `group:` and a group id.
  const pending = new Set<string>()
  let timer: ReturnType<typeof setTimeout> | undefined

  const flush = (): void => {
    clearTimeout(timer)
    timer = undefined
    if (!pending.size) {
      return
    }
    const keys = [...pending]
    pending.clear()
    onRemoveRecords({
      references: keys.filter((key) => !key.startsWith(GROUP_PREFIX)),
      groupIds: keys.flatMap((key) =>
        key.startsWith(GROUP_PREFIX) ? [key.slice(GROUP_PREFIX.length)] : []
      ),
    })
  }

  return new Plugin({
    key: OBSERVATION_INPUT_REMOVAL_KEY,
    props: {
      handleTextInput: (_view, from, to) => {
        if (from !== to) {
          typingOverRange = true
          queueMicrotask(() => {
            typingOverRange = false
          })
        }
        return false
      },
    },
    appendTransaction: (transactions, oldState, newState) => {
      if (!transactions.some((tr) => tr.docChanged)) {
        return null
      }
      const present = heldReferences(newState.doc, typeName)
      for (const key of pending) {
        if (present.has(key)) {
          pending.delete(key)
        }
      }
      if (isInputOverRecords(transactions, typingOverRange)) {
        for (const key of recordKeys(oldState.doc, typeName)) {
          if (!present.has(key)) {
            pending.add(key)
          }
        }
      }
      typingOverRange = false
      clearTimeout(timer)
      timer = pending.size ? setTimeout(flush, delayMs) : undefined
      return null
    },
    view: () => ({
      // The note is saved without the node, so its record goes now rather
      // than being left behind on the chart.
      destroy: flush,
    }),
  })
}

function isInputOverRecords(
  transactions: readonly Transaction[],
  typingOverRange: boolean
): boolean {
  const events = transactions.map((tr) => tr.getMeta('uiEvent'))
  if (events.includes('cut')) {
    return false
  }
  return typingOverRange || events.includes('paste')
}

// The records live nodes act on, as removal counts them: a node's own, and a
// panel's group.
function recordKeys(doc: PMNode, typeName: string): Set<string> {
  const keys = new Set<string>()
  doc.descendants((node) => {
    if (node.type.name === typeName) {
      for (const reference of observationReferenceStrings(node.attrs)) {
        keys.add(reference)
      }
    }
    if (node.type.name === OBSERVATION_PANEL_NODE) {
      for (const key of panelKeys(node)) {
        keys.add(key)
      }
    }
    return true
  })
  return keys
}

// Every record a node still names, set aside by freezing included, so a
// record that only moved within the document is never taken for removed.
function heldReferences(doc: PMNode, typeName: string): Set<string> {
  const references = new Set<string>()
  doc.descendants((node) => {
    if (node.type.name === OBSERVATION_PANEL_NODE) {
      for (const key of panelKeys(node)) {
        references.add(key)
      }
      return true
    }
    if (node.type.name !== typeName) {
      return true
    }
    const { reference, references: answers, frozenReference } = node.attrs
    for (const held of [
      reference as string | null,
      frozenReference as string | null,
      ...recordedObservationAnswers(answers).map((answer) => answer.reference),
      ...recordedObservationAnswers(node.attrs.frozenReferences).map(
        (answer) => answer.reference
      ),
    ]) {
      if (held) {
        references.add(held)
      }
    }
    return true
  })
  return references
}

// A recorded panel's group, and its panel record by reference as well.
function panelKeys(node: PMNode): string[] {
  const { groupId, panelReference } = node.attrs
  if (!groupId) {
    return []
  }
  return [
    `${GROUP_PREFIX}${String(groupId)}`,
    ...(panelReference ? [String(panelReference)] : []),
  ]
}
